import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, XCircle, EnvelopeSimple, DeviceMobile } from "@phosphor-icons/react";
import client from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

const AuthShell = ({ eyebrow, title, children }) => (
  <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center noise p-6">
    <div className="w-full max-w-md">
      <Link to="/" className="flex items-center gap-2 mb-8" data-testid="auth-shell-logo">
        <BrandLogo height={56} />
      </Link>
      <div className="border border-white/10 bg-[#111318] p-8">
        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— {eyebrow}</div>
        <h1 className="font-display text-3xl tracking-tighter mb-6">{title}</h1>
        {children}
      </div>
    </div>
  </div>
);

export const ForgotPassword = () => {
  const [mode, setMode] = useState("email"); // "email" | "totp"
  // Email-link path
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  // TOTP path
  const [totpEmail, setTotpEmail] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpPwd, setTotpPwd] = useState("");
  const [totpConfirm, setTotpConfirm] = useState("");
  const [totpDone, setTotpDone] = useState(false);
  const [totpLoading, setTotpLoading] = useState(false);
  const nav = useNavigate();

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await client.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    if (totpPwd !== totpConfirm) return toast.error("Passwords don't match");
    if (totpPwd.length < 6) return toast.error("Password must be at least 6 characters");
    setTotpLoading(true);
    try {
      const { data } = await client.post("/auth/reset-password-totp", {
        email: totpEmail,
        totp_code: totpCode,
        new_password: totpPwd,
      });
      setTotpDone(true);
      if (data?.recovery_code_used) {
        toast.success("Password reset — recovery code was burned, regenerate your codes after login");
      } else {
        toast.success("Password reset — log in with your new password");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setTotpLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="RESET PASSWORD"
      title={
        sent ? "Check your inbox." :
        totpDone ? "Password updated." :
        mode === "totp" ? "Use your authenticator." :
        "Forgot your password?"
      }
    >
      {/* Email-sent success */}
      {sent && (
        <div className="text-center">
          <EnvelopeSimple size={64} weight="duotone" className="text-[#FACC15] mx-auto mb-4" />
          <p className="font-mono-k text-sm text-[#A1A1AA] mb-4">If that email exists in our system, we've sent a reset link. It expires in 1 hour.</p>
          <p className="font-mono-k text-xs text-[#A1A1AA] mb-6">Didn't get it? Try your authenticator app instead →</p>
          <button onClick={() => { setSent(false); setMode("totp"); setTotpEmail(email); }} className="k-btn w-full justify-center mb-3" data-testid="forgot-switch-to-totp">
            <DeviceMobile size={16} /> USE AUTHENTICATOR INSTEAD
          </button>
          <Link to="/login" className="text-sm font-mono-k text-[#A1A1AA] hover:text-[#FACC15]" data-testid="forgot-back">← Back to login</Link>
        </div>
      )}

      {/* TOTP reset success */}
      {totpDone && (
        <div className="text-center">
          <CheckCircle size={64} weight="duotone" className="text-[#FACC15] mx-auto mb-4" />
          <p className="font-mono-k text-sm text-[#A1A1AA] mb-6">Your password has been updated. Log in with your new password.</p>
          <button onClick={() => nav("/login")} className="k-btn primary w-full justify-center" data-testid="totp-reset-to-login">
            GO TO LOGIN <ArrowRight size={16} />
          </button>
        </div>
      )}

      {/* Mode toggle + forms */}
      {!sent && !totpDone && (
        <>
          <div className="flex border border-white/10 mb-5" data-testid="forgot-mode-tabs">
            <button
              onClick={() => setMode("email")}
              className={`flex-1 py-2 text-xs font-mono-k tracking-widest-k transition-colors ${mode === "email" ? "bg-[#FACC15] text-[#0A0D14]" : "bg-transparent text-[#A1A1AA] hover:text-white"}`}
              data-testid="forgot-tab-email"
            >
              <EnvelopeSimple size={14} className="inline mr-1" /> EMAIL LINK
            </button>
            <button
              onClick={() => setMode("totp")}
              className={`flex-1 py-2 text-xs font-mono-k tracking-widest-k transition-colors ${mode === "totp" ? "bg-[#FACC15] text-[#0A0D14]" : "bg-transparent text-[#A1A1AA] hover:text-white"}`}
              data-testid="forgot-tab-totp"
            >
              <DeviceMobile size={14} className="inline mr-1" /> AUTHENTICATOR
            </button>
          </div>

          {mode === "email" ? (
            <form onSubmit={submitEmail} className="space-y-4">
              <p className="text-sm font-mono-k text-[#A1A1AA]">Enter your email and we'll send you a link to reset it. If you don't receive it within 2 minutes, switch to the <button type="button" onClick={() => setMode("totp")} className="text-[#FACC15] underline">Authenticator</button> tab.</p>
              <div>
                <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">EMAIL</label>
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="k-input mt-1" data-testid="forgot-email" />
              </div>
              <button disabled={loading} className="k-btn primary w-full justify-center" data-testid="forgot-submit">
                {loading ? "SENDING…" : "SEND RESET LINK"} <ArrowRight size={16} />
              </button>
              <div className="text-sm font-mono-k text-[#A1A1AA] text-center pt-2">
                <Link to="/login" className="text-[#FACC15] hover:underline">← Back to login</Link>
              </div>
            </form>
          ) : (
            <form onSubmit={submitTotp} className="space-y-4">
              <p className="text-sm font-mono-k text-[#A1A1AA]">If you've enabled 2FA, enter your email, the 6-digit code from your authenticator app (or a 10-char recovery code), and your new password. No email needed.</p>
              <div>
                <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">EMAIL</label>
                <input required type="email" value={totpEmail} onChange={e => setTotpEmail(e.target.value)} className="k-input mt-1" data-testid="totp-email" />
              </div>
              <div>
                <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">AUTHENTICATOR CODE</label>
                <input
                  required
                  type="text"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\s/g, "").slice(0, 10))}
                  placeholder="123 456  or  recovery code"
                  autoComplete="one-time-code"
                  className="k-input mt-1 tracking-widest font-mono text-center"
                  data-testid="totp-code"
                />
              </div>
              <div>
                <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">NEW PASSWORD</label>
                <input required type="password" minLength={6} value={totpPwd} onChange={e => setTotpPwd(e.target.value)} className="k-input mt-1" data-testid="totp-new-password" />
              </div>
              <div>
                <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">CONFIRM NEW PASSWORD</label>
                <input required type="password" minLength={6} value={totpConfirm} onChange={e => setTotpConfirm(e.target.value)} className="k-input mt-1" data-testid="totp-confirm" />
              </div>
              <button disabled={totpLoading} className="k-btn primary w-full justify-center" data-testid="totp-submit">
                {totpLoading ? "RESETTING…" : "RESET PASSWORD"} <ArrowRight size={16} />
              </button>
              <div className="text-sm font-mono-k text-[#A1A1AA] text-center pt-2">
                <Link to="/login" className="text-[#FACC15] hover:underline">← Back to login</Link>
              </div>
            </form>
          )}
        </>
      )}
    </AuthShell>
  );
};

export const ResetPassword = () => {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try { await client.post("/auth/reset-password", { token, password }); setDone(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  if (!token) {
    return <AuthShell eyebrow="RESET" title="Invalid link."><p className="font-mono-k text-sm text-[#A1A1AA]">This reset link is missing a token. Request a fresh one from the forgot-password page.</p><Link to="/forgot-password" className="k-btn primary w-full justify-center mt-6">REQUEST NEW LINK</Link></AuthShell>;
  }
  return (
    <AuthShell eyebrow="NEW PASSWORD" title={done ? "Password updated." : "Set a new password."}>
      {done ? (
        <div className="text-center">
          <CheckCircle size={64} weight="duotone" className="text-[#FACC15] mx-auto mb-4" />
          <p className="font-mono-k text-sm text-[#A1A1AA] mb-6">Your password has been updated. Log in with your new password.</p>
          <button onClick={() => nav("/login")} className="k-btn primary w-full justify-center" data-testid="reset-to-login">GO TO LOGIN <ArrowRight size={16} /></button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">NEW PASSWORD</label>
            <input required type="password" minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="k-input mt-1" data-testid="reset-password" />
          </div>
          <div>
            <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">CONFIRM PASSWORD</label>
            <input required type="password" minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)} className="k-input mt-1" data-testid="reset-confirm" />
          </div>
          <button disabled={loading} className="k-btn primary w-full justify-center" data-testid="reset-submit">{loading ? "SAVING…" : "SET PASSWORD"} <ArrowRight size={16} /></button>
        </form>
      )}
    </AuthShell>
  );
};

export const VerifyEmail = () => {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | bad
  useEffect(() => {
    if (!token) { setStatus("bad"); return; }
    client.post("/auth/verify-email", { token }).then(() => setStatus("ok")).catch(() => setStatus("bad"));
  }, [token]);
  return (
    <AuthShell eyebrow="EMAIL VERIFICATION" title={status === "ok" ? "Email verified." : status === "bad" ? "Verification failed." : "Verifying…"}>
      <div className="text-center">
        {status === "ok" && <CheckCircle size={64} weight="duotone" className="text-[#FACC15] mx-auto mb-4" />}
        {status === "bad" && <XCircle size={64} weight="duotone" className="text-red-400 mx-auto mb-4" />}
        <p className="font-mono-k text-sm text-[#A1A1AA] mb-6">
          {status === "ok" && "Your email is now verified. All account features are unlocked."}
          {status === "bad" && "This link is invalid or has already been used. Request a fresh one from the dashboard."}
          {status === "loading" && "Confirming your email…"}
        </p>
        <Link to="/dashboard" className="k-btn primary w-full justify-center" data-testid="verify-continue">CONTINUE <ArrowRight size={16} /></Link>
      </div>
    </AuthShell>
  );
};
